import { DDCell, DDRow, AdditionalInfoFieldType } from '../Types/AdditionalInformation/AdditionalInfoField';

/**
 * Type-safe field property mapping
 * Maps field types to their corresponding property names
 */
export const FIELD_PROPERTY_MAP = {
  [AdditionalInfoFieldType.TextArea]: 'text',
  [AdditionalInfoFieldType.DropDownList]: 'text',
  [AdditionalInfoFieldType.DatePicker]: 'date',
  [AdditionalInfoFieldType.TimePicker]: 'date',
  [AdditionalInfoFieldType.MultiSelect]: 'selection',
  [AdditionalInfoFieldType.Switch]: 'state',
} as const;

/**
 * Type-safe field value processors
 * Handles value transformation based on field type
 */
export const FIELD_VALUE_PROCESSORS = {
  [AdditionalInfoFieldType.TextArea]: (value: any) => value,
  [AdditionalInfoFieldType.DropDownList]: (value: any) => value,
  [AdditionalInfoFieldType.DatePicker]: (value: any) => value,
  [AdditionalInfoFieldType.TimePicker]: (value: any) => value,
  [AdditionalInfoFieldType.MultiSelect]: (value: any) => Array.isArray(value) ? value : [value],
  [AdditionalInfoFieldType.Switch]: (value: any) => Boolean(value),
} as const;


export const updateFieldByType = (field: DDCell, newValue: any): DDCell => {
  const processor = FIELD_VALUE_PROCESSORS[field.type];
  const processedValue = processor ? processor(newValue) : newValue;
  
  // Use type assertion to handle the dynamic property assignment
  const propertyName = FIELD_PROPERTY_MAP[field.type] || 'text';
  return {
    ...field,
    [propertyName]: processedValue
  } as DDCell;
};

/**
 * Creates a server payload for a field update
 * @param field - The field to create payload for
 * @param newValue - The new value to set
 * @returns Server payload object
 */
export const createServerPayload = (field: DDCell, newValue: any): DDCell => {
  const payload = { ...field };
  const processor = FIELD_VALUE_PROCESSORS[field.type];
  const processedValue = processor ? processor(newValue) : newValue;
  
  const propertyName = FIELD_PROPERTY_MAP[field.type] || 'text';
  (payload as any)[propertyName] = processedValue;
  return payload;
};

/**
 * Flattens DDRow array into DDCell array
 * @param rows - Array of DDRow objects
 * @returns Flattened array of DDCell objects
 */
export const flattenRows = (rows: DDRow[]): DDCell[] => {
  const allCells: DDCell[] = [];
  rows.forEach((row, rowIndex) => {
    row.cells.forEach((cell: DDCell) => {
      cell.rowNumber = rowIndex;
      allCells.push(cell);
    });
  });
  return allCells;
};
